import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import { HEALTH_STATES } from '../../services/health';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import HealthDetail, { type BackendHealth } from './HealthDetail';

type Tone = 'success' | 'warning' | 'error' | 'secondary';

interface Presentation {
  tone: Tone;
  label: string;
  pulse: boolean;
}

/**
 * Each state is described by one palette "tone", which drives the whole pill: the tinted
 * fill, the text color, the border and the solid dot. Signed-out shares the error tone with
 * offline, but keeps its own wording so an expired session is never mistaken for an outage.
 */
const PRESENTATION: Record<string, Presentation> = {
  [HEALTH_STATES.HEALTHY]: { tone: 'success', label: 'Connected', pulse: false },
  [HEALTH_STATES.DEGRADED]: { tone: 'warning', label: 'Degraded', pulse: false },
  [HEALTH_STATES.OFFLINE]: { tone: 'error', label: 'Offline', pulse: true },
  [HEALTH_STATES.SIGNED_OUT]: { tone: 'error', label: 'Signed out', pulse: true },
};

const CHECKING: Presentation = { tone: 'secondary', label: 'Checking…', pulse: false };

// theme.vars is always defined here: src/theme/theme.ts enables cssVariables.
const toneVars = (theme: Theme, tone: Tone) => theme.vars!.palette[tone];

/**
 * Backend status indicator for the app header: a compact pill showing whether the CMS
 * can reach its API, with the detail on hover or keyboard focus.
 */
export default function HealthIndicator() {
  const { health } = useBackendHealth() as { health?: BackendHealth };
  const { tone, label, pulse } = (health?.state && PRESENTATION[health.state]) || CHECKING;

  return (
    <Tooltip
      title={<HealthDetail health={health} />}
      placement='bottom'
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 3,
            border: 1,
            borderColor: 'divider',
            p: 1.5,
            maxWidth: 320,
          },
        },
        arrow: { sx: { color: 'background.paper' } },
      }}
    >
      <Box
        component='button'
        type='button'
        data-tone={tone}
        aria-label={`Backend status: ${label}`}
        sx={(theme) => {
          const { mainChannel: channel, dark, light } = toneVars(theme, tone);
          return {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.375,
            borderRadius: 999,
            border: `1px solid rgba(${channel} / 0.35)`,
            bgcolor: `rgba(${channel} / 0.12)`,
            color: dark,
            cursor: 'default',
            font: 'inherit',
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: 1.35,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
            transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
            '&:hover': { boxShadow: 1 },
            '&:focus-visible': { outline: 0, boxShadow: `0 0 0 3px rgba(${channel} / 0.25)` },
            ...theme.applyStyles('dark', { color: light }),
          };
        }}
      >
        <Box
          component='span'
          data-testid='health-indicator-dot'
          className={pulse ? 'health-indicator-dot-pulse' : undefined}
          sx={(theme) => {
            const { mainChannel: channel } = toneVars(theme, tone);
            return {
              width: 7,
              height: 7,
              borderRadius: '50%',
              flex: '0 0 auto',
              bgcolor: `${tone}.main`,
              boxShadow: `0 0 0 3px rgba(${channel} / 0.22)`,
              ...(pulse && {
                animation: 'health-indicator-pulse 1.9s ease-out infinite',
                '@keyframes health-indicator-pulse': {
                  '0%': { boxShadow: `0 0 0 0 rgba(${channel} / 0.5)` },
                  '70%': { boxShadow: '0 0 0 7px transparent' },
                  '100%': { boxShadow: '0 0 0 0 transparent' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                  boxShadow: `0 0 0 3px rgba(${channel} / 0.5)`,
                },
              }),
            };
          }}
        />
        <Box component='span' sx={{ transform: 'translateY(0.5px)' }}>
          {label}
        </Box>
      </Box>
    </Tooltip>
  );
}
